import { TOTAL_TILES } from '../types/game-state.ts';
import {
  INITIAL_DOWN_COUNT,
  INITIAL_UP_COUNT,
  LANE_SIZE,
  type LaneSlot,
  type PlayerLane,
} from '../types/lane.ts';
import type { Tile } from '../types/tile.ts';
import { type RNG, shuffle } from './random.ts';
import { rotationCandidates } from './rotation.ts';

export type LaneSetup = {
  lane: PlayerLane;
  remainingDeck: Tile[];
};

const PROPORTIONAL_TOLERANCES = [4, 6, 8, LANE_SIZE] as const;
const MAX_GAP_RATIO = 12;

function idealSlotIndex(n: number): number {
  return Math.round(((n - 1) * (LANE_SIZE - 1)) / (TOTAL_TILES - 1));
}

/**
 * 인접 공개 타일쌍의 (사이 가능 숫자 수) / (사이 비공개 칸 수) ≤ MAX_GAP_RATIO 검증.
 * slotGap=1(공개끼리 붙어 있음)은 검사 대상 아님.
 */
function gapRatiosValid(slots: readonly LaneSlot[]): boolean {
  let prev: { idx: number; n: number } | null = null;
  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i];
    if (!slot || slot.tile.face !== 'UP' || slot.tile.resolvedNumber === null) continue;
    const cur = { idx: i, n: slot.tile.resolvedNumber };
    if (prev !== null) {
      const slotGap = cur.idx - prev.idx;
      const numGap = cur.n - prev.n;
      if (slotGap >= 2) {
        const between = slotGap - 1;
        const possible = numGap - 1;
        if (possible > MAX_GAP_RATIO * between) return false;
      }
    }
    prev = cur;
  }
  return true;
}

/**
 * 숫자 N이 lane(LANE_SIZE칸)에 들어갈 때 수론적으로 가능한 슬롯 인덱스 범위.
 * N 왼쪽 슬롯 수 = i, 오른쪽 슬롯 수 = LANE_SIZE - 1 - i.
 *  - 왼쪽엔 1..N-1 중 일부만 가능 → i ≤ N - 1
 *  - 오른쪽엔 N+1..TOTAL_TILES 중 일부만 가능, 수 = TOTAL_TILES - N
 *    → LANE_SIZE - 1 - i ≤ TOTAL_TILES - N  →  i ≥ N - 1 - (TOTAL_TILES - LANE_SIZE)
 *
 * 예: N=100 → i ∈ [21, 21]; N=3 → i ∈ [0, 2]; N=50 → i ∈ [0, 21] (자유).
 */
function feasibleSlotRange(n: number): { min: number; max: number } {
  const min = Math.max(0, n - 1 - (TOTAL_TILES - LANE_SIZE));
  const max = Math.min(LANE_SIZE - 1, n - 1);
  return { min, max };
}

/**
 * 17 비공개 + 5 공개 셋업.
 * RULES.md §3 + §3.1 (항상 가능).
 *
 * @param deck - 셔플된 타일 배열 (배열 끝이 top)
 * @param rng - 결정론적 무작위 생성기
 * @returns 22칸 줄 + 남은 deck
 */
export function setupPlayerLane(deck: Tile[], rng: RNG): LaneSetup {
  if (deck.length < INITIAL_DOWN_COUNT + INITIAL_UP_COUNT) {
    throw new Error(
      `deck has ${deck.length} tiles, need at least ${INITIAL_DOWN_COUNT + INITIAL_UP_COUNT}`,
    );
  }

  const remaining = deck.slice();

  // 1. 17장을 비공개로 가져와 자기 줄에 일렬 배치
  const slots: LaneSlot[] = [];
  for (let i = 0; i < INITIAL_DOWN_COUNT; i++) {
    const tile = remaining.pop();
    if (!tile) throw new Error('unreachable: deck checked above');
    slots.push({ tile: { ...tile, face: 'DOWN', resolvedNumber: null } });
  }

  // 2. 5장을 공개. 각 타일은 hard bound(수론적 가능 범위) + 비례 tolerance를 만족해야 하며
  //    splice pos = 최종 lane 인덱스가 되도록 회전 결정 후 오름차순 정렬해 끼워넣는다.
  //    회전 후보 조합(2^k, k = 회전 가능 타일 수)을 backtracking으로 탐색.
  const upTiles: Tile[] = [];
  for (let i = 0; i < INITIAL_UP_COUNT; i++) {
    const tile = remaining.pop();
    if (!tile) throw new Error('unreachable: deck checked above');
    upTiles.push(tile);
  }
  if (!placeUpTiles(slots, upTiles, rng)) {
    throw new Error(
      `unreachable: §3.1 setup failed — backtracking exhausted (deck head=${upTiles.map((t) => t.number).join(',')})`,
    );
  }

  if (slots.length !== LANE_SIZE) {
    throw new Error(`lane size mismatch: ${slots.length}, expected ${LANE_SIZE}`);
  }

  return {
    lane: { slots },
    remainingDeck: remaining,
  };
}

/**
 * 5장 공개 타일을 회전 결정 후 오름차순으로 끼워넣는다.
 * 회전 후보 조합(∏|rotationCandidates(tile)|)을 RNG로 셔플 후 backtracking 탐색.
 *
 * 정렬 끼워넣기 → splice pos = 최종 lane 인덱스. 이로써 각 타일의 hard bound
 * (feasibleSlotRange) 검사가 단계 도중에도 최종 인덱스 기준으로 정확.
 *
 * @returns 성공 시 true (slots는 in-place로 변경됨), 실패 시 false (slots 원복).
 */
function placeUpTiles(slots: LaneSlot[], tiles: readonly Tile[], rng: RNG): boolean {
  // 각 타일별 회전 후보 — RNG로 셔플해 시도 순서를 무작위화.
  const candidatesPerTile = tiles.map((t) => shuffle(rotationCandidates(t.number), rng));
  const resolved: number[] = new Array(tiles.length).fill(0);

  function tryAssign(idx: number): boolean {
    if (idx >= tiles.length) {
      // 모든 회전 결정 완료 → 정렬해 끼워넣기 시도.
      return tryInsertSorted();
    }
    const cands = candidatesPerTile[idx] ?? [];
    for (const n of cands) {
      resolved[idx] = n;
      if (tryAssign(idx + 1)) return true;
    }
    return false;
  }

  function tryInsertSorted(): boolean {
    // 5장 회전 결정 → distinct 검사(중복이면 어차피 오름차순 불가).
    const set = new Set(resolved);
    if (set.size !== resolved.length) return false;

    // (resolved, original tile) 쌍을 숫자 오름차순 정렬.
    const order = tiles.map((t, i) => ({ tile: t, n: resolved[i] as number }));
    order.sort((a, b) => a.n - b.n);

    const inserted: number[] = []; // splice 위치 기록(원복용)

    function tryStep(step: number): boolean {
      if (step >= order.length) {
        // 모든 삽입 완료 → 인접 공개쌍 갭 비율 검증.
        return gapRatiosValid(slots);
      }
      const entry = order[step];
      if (!entry) return false;
      const { tile, n } = entry;
      const { min, max } = feasibleSlotRange(n);
      const positions = findInsertablePositions(slots, n).filter((p) => p >= min && p <= max);

      // 비례 tolerance — 작은 tol부터 후보 있으면 채택.
      let choices = positions;
      for (const tol of PROPORTIONAL_TOLERANCES) {
        const filtered = positions.filter((p) => Math.abs(p - idealSlotIndex(n)) <= tol);
        if (filtered.length > 0) {
          choices = filtered;
          break;
        }
      }
      if (choices.length === 0) return false;

      // 위치 후보를 RNG 셔플 후 backtracking — 갭 비율로 막히면 다음 후보 시도.
      const shuffled = shuffle(choices, rng);
      const upTile: Tile = { ...tile, face: 'UP', resolvedNumber: n };
      for (const pos of shuffled) {
        slots.splice(pos, 0, { tile: upTile });
        inserted.push(pos);
        if (tryStep(step + 1)) return true;
        slots.splice(pos, 1);
        inserted.pop();
      }
      return false;
    }

    if (tryStep(0)) return true;
    rollback(inserted);
    return false;
  }

  function rollback(inserted: readonly number[]): void {
    for (let i = inserted.length - 1; i >= 0; i--) {
      const pos = inserted[i] as number;
      slots.splice(pos, 1);
    }
  }

  return tryAssign(0);
}

/**
 * 현재 slots 배열에 N을 끼워넣을 수 있는 위치 후보 (인덱스 0..slots.length).
 * 끼워넣은 후 모든 *공개* 타일이 좌→우 오름차순이어야 함.
 * 비공개 타일은 비교 대상이 아님.
 */
function findInsertablePositions(slots: readonly LaneSlot[], n: number): number[] {
  const out: number[] = [];
  for (let pos = 0; pos <= slots.length; pos++) {
    let leftMax = Number.NEGATIVE_INFINITY;
    let rightMin = Number.POSITIVE_INFINITY;
    for (let i = 0; i < pos; i++) {
      const slot = slots[i];
      if (slot && slot.tile.face === 'UP' && slot.tile.resolvedNumber !== null) {
        if (slot.tile.resolvedNumber > leftMax) leftMax = slot.tile.resolvedNumber;
      }
    }
    for (let i = pos; i < slots.length; i++) {
      const slot = slots[i];
      if (slot && slot.tile.face === 'UP' && slot.tile.resolvedNumber !== null) {
        if (slot.tile.resolvedNumber < rightMin) rightMin = slot.tile.resolvedNumber;
      }
    }
    if (leftMax < n && n < rightMin) out.push(pos);
  }
  return out;
}

type FaceLaneLike = {
  slots: ReadonlyArray<{ tile: { face: 'UP' | 'DOWN' } }>;
};

export function laneIsFullyRevealed(lane: FaceLaneLike): boolean {
  return lane.slots.every((s) => s.tile.face === 'UP');
}

export function laneRevealedCount(lane: FaceLaneLike): number {
  return lane.slots.filter((s) => s.tile.face === 'UP').length;
}

export function laneDownCount(lane: FaceLaneLike): number {
  return lane.slots.filter((s) => s.tile.face === 'DOWN').length;
}

/**
 * 자기 줄의 공개된 타일들이 좌→우 오름차순인지 검증.
 * 셋업 / 액션 후 무결성 체크용.
 */
export function laneAscendingValid(lane: PlayerLane): boolean {
  let prev = Number.NEGATIVE_INFINITY;
  for (const slot of lane.slots) {
    if (slot.tile.face === 'UP' && slot.tile.resolvedNumber !== null) {
      if (slot.tile.resolvedNumber <= prev) return false;
      prev = slot.tile.resolvedNumber;
    }
  }
  return true;
}
